/**
 * `<gallery-lightbox>` — wraps a gallery and opens clicked images in a fullscreen dialog.
 *
 * Any child element with `data-lightbox-src` is treated as a gallery item.
 * Clicking it opens a native `<dialog>` with the full-size image.
 *
 * The lightbox only enables itself while the grid is showing more than one
 * column. At a single column the image already fills the viewport width, so
 * opening a fullscreen copy of it achieves nothing and just swallows the tap —
 * which is what the gallery does on phones, where `columns_mobile` is 1.
 *
 * Gated on the live column count rather than a hardcoded width so that changing
 * the section's column settings keeps working without touching this file.
 *
 * @attr {string} data-lightbox-src  Full-size image URL (on each gallery item)
 * @attr {string} data-lightbox-alt  Alt text for the full-size image (on each gallery item)
 * @attr {string} data-lightbox-width  Width to display the image at, in CSS px (on each gallery item)
 * @attr {string} data-lightbox-ratio  Source aspect ratio, width / height (on each gallery item)
 * @attr {string} data-lightbox-enabled  Set by this component while the lightbox is active; styling hook
 */
class GalleryLightbox extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #dialogImg = null;
  /** @type {HTMLElement[]} */
  #items = [];
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number} */
  #loadGeneration = 0;
  /**
   * Full-size images already requested, keyed by URL, each resolving once decoded.
   * @type {Map<string, Promise<void>>}
   */
  #preloads = new Map();
  /** @type {Set<string>} */
  #decoded = new Set();

  connectedCallback() {
    this.#items = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('[data-lightbox-src]')]
    );
    if (!this.#items.length) return;
    this.#buildDialog();
    this.addEventListener('click', this.#handleItemClick);
    this.addEventListener('pointerover', this.#handleItemHover);
    this.#updateEnabled();
    window.addEventListener('resize', this.#updateEnabled);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleItemClick);
    this.removeEventListener('pointerover', this.#handleItemHover);
    window.removeEventListener('resize', this.#updateEnabled);
    this.#dialog?.remove();
    this.#dialog = null;
  }

  /**
   * Columns the grid is currently showing. Mirrors the breakpoints and
   * defaults in masonry-grid.js, which owns the actual layout.
   * @returns {number}
   */
  get #columnCount() {
    const grid = this.querySelector('masonry-grid');
    if (!(grid instanceof HTMLElement)) return 1;

    const desktop = Number(grid.dataset.columnsDesktop ?? 4);
    const tablet = Number(grid.dataset.columnsTablet ?? 3);
    const mobile = Number(grid.dataset.columnsMobile ?? 2);
    if (window.innerWidth >= 990) return desktop;
    if (window.innerWidth >= 750) return tablet;
    return mobile;
  }

  /** @returns {boolean} */
  get #enabled() {
    return this.#columnCount > 1;
  }

  /**
   * Reflects the enabled state onto the host so CSS can drop the zoom cursor
   * with it. Only writes on an actual change, since this runs on resize.
   */
  #updateEnabled = () => {
    const enabled = this.#enabled;
    if (enabled === this.hasAttribute('data-lightbox-enabled')) return;
    this.toggleAttribute('data-lightbox-enabled', enabled);
  };

  #buildDialog() {
    this.#dialog = document.createElement('dialog');
    this.#dialog.className = 'gallery-dialog';
    this.#dialog.innerHTML = `
      <button class="gallery-dialog__close" aria-label="Close">&times;</button>
      <button class="gallery-dialog__prev" aria-label="Previous">&#8249;</button>
      <img class="gallery-dialog__img" src="" alt="" />
      <button class="gallery-dialog__next" aria-label="Next">&#8250;</button>
      <p class="gallery-dialog__caption"></p>
      <p class="gallery-dialog__counter"></p>
    `;

    this.#dialogImg = this.#dialog.querySelector('.gallery-dialog__img');

    this.#dialog.querySelector('.gallery-dialog__close')
      .addEventListener('click', () => this.#close());
    this.#dialog.querySelector('.gallery-dialog__prev')
      .addEventListener('click', () => this.#navigate(-1));
    this.#dialog.querySelector('.gallery-dialog__next')
      .addEventListener('click', () => this.#navigate(1));

    // Clicking the backdrop (the dialog element itself, not its children) closes the dialog
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#close();
    });

    // Arrow key navigation (ESC is handled natively by showModal)
    this.#dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#navigate(-1);
      if (event.key === 'ArrowRight') this.#navigate(1);
    });

    // Append to body so it's never clipped by overflow/stacking contexts
    document.body.appendChild(this.#dialog);
  }

  /**
   * Starts fetching the full-size image as soon as the pointer is over an item.
   * The gap between hover and click is usually longer than the download, so the
   * lightbox can open straight onto the sharp image.
   * @type {(event: PointerEvent) => void}
   */
  #handleItemHover = (event) => {
    if (!this.#enabled || !(event.target instanceof Element)) return;
    const item = event.target.closest('[data-lightbox-src]');
    if (item instanceof HTMLElement) this.#preload(item.dataset.lightboxSrc);
  };

  /**
   * Fetches and decodes a full-size image once, however often it is asked for.
   * @param {string | undefined} src
   * @returns {Promise<void>}
   */
  #preload(src) {
    if (!src) return Promise.resolve();
    let pending = this.#preloads.get(src);
    if (!pending) {
      const image = new Image();
      image.src = src;
      pending = image
        .decode()
        .then(() => {
          this.#decoded.add(src);
        })
        .catch(() => {
          // A failed preload is retried on the next request rather than cached.
          this.#preloads.delete(src);
        });
      this.#preloads.set(src, pending);
    }
    return pending;
  }

  /** @type {(event: MouseEvent) => void} */
  #handleItemClick = (event) => {
    const item = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('[data-lightbox-src]')
        : null
    );
    if (!item) return;
    // At one column the tap should do nothing at all — not even be swallowed —
    // so links and anything else inside the item keep working.
    if (!this.#enabled) return;
    event.preventDefault();
    this.#currentIndex = this.#items.indexOf(item);
    this.#open();
  };

  #open() {
    const item = this.#items[this.#currentIndex];
    if (!item || !this.#dialog || !this.#dialogImg) return;

    const hiResSrc = item.dataset.lightboxSrc ?? '';
    const thumbEl = item.querySelector('img');
    const thumbSrc = thumbEl?.currentSrc || thumbEl?.src || '';
    const img = this.#dialogImg;
    const gen = ++this.#loadGeneration;

    // Size the box before any pixels arrive, so the swap below cannot resize it.
    const ratio = Number(item.dataset.lightboxRatio);
    const width = Number(item.dataset.lightboxWidth);
    if (ratio > 0) img.style.setProperty('--lightbox-ratio', String(ratio));
    if (width > 0) img.style.setProperty('--lightbox-width', `${width}px`);

    img.style.opacity = '1';
    img.alt = item.dataset.lightboxAlt ?? '';

    if (!hiResSrc || this.#decoded.has(hiResSrc)) {
      // Usually the case: it was fetched while the pointer hovered the item.
      img.src = hiResSrc || thumbSrc;
    } else {
      // Show the thumbnail at full size meanwhile, then swap in the sharp file
      // once it is decoded, so the swap is a single frame.
      img.src = thumbSrc;
      this.#preload(hiResSrc).then(() => {
        if (this.#loadGeneration === gen && this.#decoded.has(hiResSrc)) img.src = hiResSrc;
      });
    }

    // Arrow-key and button navigation go to a neighbour next; have it ready.
    this.#preload(this.#items[this.#currentIndex + 1]?.dataset.lightboxSrc);
    this.#preload(this.#items[this.#currentIndex - 1]?.dataset.lightboxSrc);

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__caption'))
      .textContent = item.querySelector('.gallery__caption')?.textContent ?? '';

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__counter'))
      .textContent = `${this.#currentIndex + 1} / ${this.#items.length}`;

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__prev'))
      .hidden = this.#currentIndex === 0;

    /** @type {HTMLElement} */ (this.#dialog.querySelector('.gallery-dialog__next'))
      .hidden = this.#currentIndex === this.#items.length - 1;

    this.#dialog.showModal();
  }

  /** @param {-1 | 1} direction */
  #navigate(direction) {
    const next = this.#currentIndex + direction;
    if (next < 0 || next >= this.#items.length) return;
    this.#currentIndex = next;
    this.#open();
  }

  #close() {
    this.#dialog?.close();
  }
}

if (!customElements.get('gallery-lightbox')) {
  customElements.define('gallery-lightbox', GalleryLightbox);
}
